import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Iconify, DropdownWrapper, Tooltip } from "@repo/design-system/components/ui";
import { useTheme } from "../../contexts/index.jsx";
import { useGetStudentNotificationListQuery } from "../../app/services/NotificationAPI";
import { getAuthProps } from "../../services/auth/authService";
import { useBrowserNotification } from "../../hooks/useBrowserNotification";
import "./NotificationMenu.scss";

// How often the header re-checks the notification API for new items
const NOTIFICATION_POLLING_INTERVAL = 30000;

// More new items than this in one poll are grouped into a single browser notification
const MAX_INDIVIDUAL_BROWSER_NOTIFICATIONS = 3;

const getUserStorageId = () => {
  const userData = getAuthProps();
  return userData?.uskId || userData?.id || userData?.email || "user";
};

// Last notification count the student has seen (opened the dropdown), stored per user
const getSeenCountKey = () => `notificationSeenCount_${getUserStorageId()}`;

// Highest notification id already shown as a browser notification, stored per user
const getLastNotifiedIdKey = () => `notificationLastBrowserId_${getUserStorageId()}`;

const readLastNotifiedId = () => {
  try {
    const value = localStorage.getItem(getLastNotifiedIdKey());
    return value === null ? null : Number(value);
  } catch {
    return null;
  }
};

const writeLastNotifiedId = (id) => {
  try {
    localStorage.setItem(getLastNotifiedIdKey(), String(id));
  } catch {
    // Storage unavailable — a reload may re-show the latest browser notification
  }
};

const readSeenCount = () => {
  try {
    const value = localStorage.getItem(getSeenCountKey());
    return value === null ? null : Number(value);
  } catch {
    return null;
  }
};

const writeSeenCount = (count) => {
  try {
    localStorage.setItem(getSeenCountKey(), String(count));
  } catch {
    // Storage unavailable (private mode etc.) — the dot simply won't persist
  }
};

const formatRelativeTime = (createdAt) => {
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return "";

  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - createdDate.getTime()) / 1000));
  if (elapsedSeconds < 60) return `${Math.max(1, elapsedSeconds)}s ago`;
  if (elapsedSeconds < 3600) return `${Math.floor(elapsedSeconds / 60)}m ago`;
  if (elapsedSeconds < 86400) return `${Math.floor(elapsedSeconds / 3600)}h ago`;
  if (elapsedSeconds < 172800) return "Yesterday";
  return `${Math.floor(elapsedSeconds / 86400)}d ago`;
};

// Subcomponent for handling image loading and fallback
const NotificationThumbnail = ({ image, title }) => {
  const [imageError, setImageError] = useState(false);

  if (image && !imageError) {
    return (
      <div className="notif-image-wrapper">
        <img src={image} alt={title || "Notification"}
          className="notif-image" onError={() => setImageError(true)}
          loading="lazy" />
      </div>
    );
  }

  return (
    <div className="notif-image-wrapper notif-image-placeholder">
      <div className="placeholder-icon-box">
        <Iconify icon="solar:bell-bold-duotone" width={20} />
      </div>
    </div>
  );
};

NotificationThumbnail.propTypes = {
  image: PropTypes.string,
  title: PropTypes.string,
};

const NotificationMenu = ({ onViewAllAnnouncements }) => {
  const { theme } = useTheme();

  const { data } = useGetStudentNotificationListQuery({
    page: 0,
    size: 5,
    searchText: "",
    sortDir: "desc",
    sortBy: "id",
  }, {
    // Keep polling in background tabs too, so browser notifications arrive while the student is elsewhere
    pollingInterval: NOTIFICATION_POLLING_INTERVAL,
    refetchOnFocus: true,
  });
  const notifications = data?.data?.content ?? [];
  const totalRecords = data?.data?.totalRecords ?? data?.totalRecords;

  const { permission, requestPermission, showNotification } = useBrowserNotification();

  // Ask for browser notification permission as soon as the student lands on the page.
  // Chrome/Edge show the prompt right away; Firefox/Safari only allow it from a user gesture,
  // so if it's still undecided we ask again on the student's first click/keypress.
  useEffect(() => {
    if (permission !== "default") return undefined;

    requestPermission();

    const askOnFirstInteraction = () => {
      requestPermission();
      removeListeners();
    };
    const removeListeners = () => {
      document.removeEventListener("click", askOnFirstInteraction, true);
      document.removeEventListener("keydown", askOnFirstInteraction, true);
    };

    document.addEventListener("click", askOnFirstInteraction, true);
    document.addEventListener("keydown", askOnFirstInteraction, true);
    return removeListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a browser notification for every item newer than the last one already notified
  useEffect(() => {
    if (!data?.data?.content) return;

    const latestId = notifications.reduce((maxId, item) => Math.max(maxId, Number(item.id) || 0), 0);
    const lastNotifiedId = readLastNotifiedId();

    // First load — what is already there is old news, only track it
    if (lastNotifiedId === null || latestId <= lastNotifiedId) {
      if (lastNotifiedId === null) writeLastNotifiedId(latestId);
      return;
    }
    writeLastNotifiedId(latestId);

    if (permission !== "granted") return;

    const newItems = notifications.filter((item) => Number(item.id) > lastNotifiedId);
    if (newItems.length > MAX_INDIVIDUAL_BROWSER_NOTIFICATIONS) {
      showNotification({
        title: "New notifications",
        body: `You have ${newItems.length} new notifications`,
        tag: `notification-group-${latestId}`,
        onClick: onViewAllAnnouncements,
      });
      return;
    }

    newItems.forEach((item) => {
      showNotification({
        title: item.title,
        body: item.description,
        image: item.url || undefined,
        tag: `notification-${item.id}`,
        onClick: onViewAllAnnouncements,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Red dot when the API now has more notifications than the student last saw
  const [hasNewNotification, setHasNewNotification] = useState(false);

  useEffect(() => {
    if (totalRecords == null) return;
    const seenCount = readSeenCount();
    // First visit — treat what is already there as seen
    if (seenCount === null) {
      writeSeenCount(totalRecords);
      return;
    }
    setHasNewNotification(totalRecords > seenCount);
  }, [totalRecords]);

  // Opening the dropdown marks everything as seen and removes the dot
  const handleToggleClick = () => {
    if (totalRecords == null) return;
    writeSeenCount(totalRecords);
    setHasNewNotification(false);
  };

  return (
    <div className="notification-menu-wrapper">
      <DropdownWrapper
        direction="left"
        gap={10}
        toggleElement={
          <div className="notification-toggle-container" onClick={handleToggleClick}>
            <Tooltip label="Notifications" targetId="header-notification-button" />
            <div className="header-button notification-bell-button" id="header-notification-button"
              aria-label="Notifications" role="button" tabIndex={0} >
              <Iconify icon="solar:bell-outline" width={20} />
              {hasNewNotification && <span className="notification-new-dot" aria-label="New notifications" />}
            </div>
          </div>
        }
      >
        <div className={`notification-dropdown-panel ${theme}`}>
          {/* Header */}
          <div className="notification-panel-header">
            <div className="header-title-group">
              <div className="header-icon-box">
                <Iconify icon="solar:bell-bold-duotone" width={20} />
              </div>
              <div className="header-text-box">
                <div className="title-row">
                  <span className="main-title">Notifications</span>
                </div>
                {/* <span className="sub-title">Stay updated with latest announcements & alerts</span> */}
              </div>
            </div>
          </div>

          {/* Browser notification opt-in — the permission prompt needs a click, so it lives here */}
          {permission === "default" && (
            <div className="notification-browser-permission">
              <Iconify icon="solar:monitor-smartphone-linear" width={18} className="permission-icon" />
              <span className="permission-text">Get desktop alerts for new notifications</span>
              <button type="button" className="permission-enable-btn" onClick={requestPermission}>
                Enable
              </button>
            </div>
          )}
          {permission === "denied" && (
            <div className="notification-browser-permission is-denied">
              <Iconify icon="solar:bell-off-linear" width={18} className="permission-icon" />
              <span className="permission-text">
                Desktop alerts are blocked. Allow notifications for this site in your browser settings.
              </span>
            </div>
          )}

          {/* Notification Items List */}
          <div className="notification-list-container">
            {notifications.length > 0 ? (
              <div className="notification-items-list">
                {notifications.map((item) => (
                  <div key={item.id} className="notification-item">
                    <NotificationThumbnail image={item.url} title={item.title} />

                    <div className="notif-content">
                      <div className="notif-top-row">
                        <h4 className="notif-title">{item.title}</h4>
                        <span className="notif-time">{formatRelativeTime(item.createdAt)}</span>
                      </div>
                      <p className="notif-desc">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="notification-empty-state">
                <div className="empty-icon-circle">
                  <Iconify icon="solar:bell-off-bold-duotone" width={32} />
                </div>
                <h5 className="empty-title">No notifications</h5>
                <p className="empty-subtitle">
                  You are all caught up! There are no new notifications at this time.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="notification-panel-footer">
            <button
              type="button"
              className="view-all-link"
              onClick={() => {
                if (onViewAllAnnouncements) {
                  onViewAllAnnouncements();
                }
              }}
            >
              <span>View All Updates</span>
              <Iconify icon="solar:arrow-right-linear" width={14} />
            </button>
          </div>
        </div>
      </DropdownWrapper>
    </div>
  );
};

NotificationMenu.propTypes = {
  onViewAllAnnouncements: PropTypes.func,
};

export default NotificationMenu;
